import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowDown,
  ArrowRight,
  Binary,
  Code2,
  Layers3,
  Mail,
  MonitorUp,
  ScanSearch,
  Smartphone,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "./i18n";
import { copy, projects } from "./siteContent";
import ContactDialog from "./components/ContactDialog";
import ProjectCard from "./components/ProjectCard";

const iconSet = [Smartphone, Layers3, MonitorUp, Code2];

export default function LandingPage() {
  const { language } = useLanguage();
  const reduceMotion = useReducedMotion();
  const [contactOpen, setContactOpen] = useState(false);
  const t = copy[language];
  const appProjects = projects.filter((project) => project.type === "app");
  const caseProjects = projects.filter((project) => project.type === "case");
  const reveal = {
    initial: reduceMotion ? false : { opacity: 0, y: 26 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.18 },
    transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
  };

  return (
    <main>
      <section className="hero-section" id="top">
        <div className="hero-grid page-shell">
          <motion.div
            className="hero-copy"
            initial={reduceMotion ? false : { opacity: 0, y: 34 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <p className="eyebrow"><span />{t.hero.eyebrow}</p>
            <h1>
              {t.hero.title.map((line, index) => (
                <span key={line} className={index === 1 ? "hero-accent-line" : ""}>{line}</span>
              ))}
            </h1>
            <p className="hero-lead">{t.hero.lead}</p>
            <div className="hero-actions">
              <a className="button button-accent" href="#projects">
                {t.hero.primary}<ArrowRight size={18} />
              </a>
              <button className="text-link" type="button" onClick={() => setContactOpen(true)}>
                {t.hero.secondary}<ArrowRight size={17} />
              </button>
            </div>
          </motion.div>

          <motion.div
            className="hero-visual"
            initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.12, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <img src="/mvp.png" alt="" />
            <div className="hero-visual-wash" />
            <div className="hero-visual-topline">
              <span>{t.hero.visualLabel}</span>
              <span>01 — 04</span>
            </div>
            <div className="hero-visual-content">
              <p>{t.hero.visualTitle.split("\n").map((line) => <span key={line}>{line}</span>)}</p>
              <span>{t.hero.visualMeta}</span>
            </div>
            <div className="hero-orbit" aria-hidden="true"><span /><i /></div>
          </motion.div>
        </div>
        <a className="hero-scroll" href="#intro"><ArrowDown size={17} />{t.hero.scroll}</a>
      </section>

      <section className="intro-section" id="intro">
        <div className="page-shell">
          <motion.div className="section-heading section-heading-split" {...reveal}>
            <p className="section-label">{t.intro.label}</p>
            <div>
              <h2>{t.intro.title}</h2>
              <p>{t.intro.body}</p>
            </div>
          </motion.div>
          <div className="value-grid">
            {t.intro.values.map(([number, title, body], index) => {
              const Icon = [ScanSearch, Code2, Layers3][index];
              return (
                <motion.article key={number} className="value-card" {...reveal} transition={{ ...reveal.transition, delay: index * 0.08 }}>
                  <div className="value-card-top"><span>{number}</span><Icon size={22} strokeWidth={1.6} /></div>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="service-section" id="services">
        <div className="page-shell">
          <motion.div className="service-header" {...reveal}>
            <div>
              <p className="section-label section-label-light">{t.service.label}</p>
              <h2>{t.service.title}</h2>
            </div>
            <p>{t.service.lead}</p>
          </motion.div>

          <div className="service-layout">
            <motion.div className="service-summary" {...reveal}>
              <div className="service-symbol" aria-hidden="true"><Binary size={34} /><span /></div>
              <p>{t.service.description}</p>
              <div className="service-platforms">
                {t.service.platforms.map((platform) => <span key={platform}>{platform}</span>)}
              </div>
              <button
                className="button button-light"
                type="button"
                onClick={() => setContactOpen(true)}
              >
                {t.service.cta}<ArrowRight size={18} />
              </button>
            </motion.div>

            <div className="process-list">
              {t.service.stages.map(([number, title, body], index) => {
                const Icon = iconSet[index];
                return (
                  <motion.article key={number} className="process-row" {...reveal} transition={{ ...reveal.transition, delay: index * 0.06 }}>
                    <span className="process-number">{number}</span>
                    <div className="process-icon"><Icon size={21} /></div>
                    <div><h3>{title}</h3><p>{body}</p></div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="projects-section" id="projects">
        <div className="page-shell">
          <motion.div className="section-heading work-heading" {...reveal}>
            <div>
              <p className="section-label">{t.work.label}</p>
              <h2>{t.work.title}</h2>
            </div>
            <p>{t.work.body}</p>
          </motion.div>
          <div className="projects-grid">
            {appProjects.map((project, index) => (
              <motion.div
                className={index === 0 ? "project-slot project-slot-featured" : "project-slot"}
                key={project.slug}
                {...reveal}
                transition={{ ...reveal.transition, delay: Math.min(index * 0.08, 0.2) }}
              >
                <ProjectCard
                  project={project}
                  content={project.content[language]}
                  action={t.work.open}
                  featured={index === 0}
                  index={index}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="cases-section" id="cases">
        <div className="page-shell cases-shell">
          <motion.div className="cases-intro" {...reveal}>
            <p className="section-label section-label-light">{t.cases.label}</p>
            <h2>{t.cases.title}</h2>
            <p>{t.cases.body}</p>
            <div className="cases-scanline" aria-hidden="true"><ScanSearch size={32} /></div>
          </motion.div>
          <div className="cases-list">
            {caseProjects.map((project, index) => {
              const content = project.content[language];
              return (
                <motion.div key={project.slug} {...reveal} transition={{ ...reveal.transition, delay: index * 0.08 }}>
                  <Link className="case-row" to={`/projects/${project.slug}`}>
                    <span className="case-number">0{index + 1}</span>
                    <div className="case-thumb"><img src={project.image} alt="" loading="lazy" /></div>
                    <div className="case-title"><p>{content.category}</p><h3>{content.title}</h3></div>
                    <span className="case-action">{t.cases.open}<ArrowRight size={17} /></span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <motion.div className="cta-card page-shell" {...reveal}>
          <div>
            <p className="section-label">{t.cta.label}</p>
            <h2>{t.cta.title}</h2>
            <p>{t.cta.body}</p>
          </div>
          <button className="button button-dark" type="button" onClick={() => setContactOpen(true)}>
            <Mail size={18} />{t.cta.button}
          </button>
        </motion.div>
      </section>
      <ContactDialog open={contactOpen} onClose={() => setContactOpen(false)} />
    </main>
  );
}
